import { useEffect, useRef, useState } from 'react';
import { animate, motion, useInView, useReducedMotion } from 'framer-motion';
import { Clock, Cpu, ShieldCheck, Star } from 'lucide-react';

const STATS = [
  { value: 15, suffix: '+', label: 'Años de experiencia', icon: Star },
  { value: 500, suffix: '+', label: 'Guardias capacitados', icon: ShieldCheck },
  { value: 24, suffix: 'h', label: 'Operación continua', icon: Clock },
  { value: 100, suffix: '%', label: 'Respaldado por tecnología', icon: Cpu },
];

function CountUp({ value, suffix }: { value: number; suffix: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const reducedMotion = useReducedMotion();
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (reducedMotion) {
      setDisplay(value);
      return;
    }
    const controls = animate(0, value, {
      duration: 1.6,
      ease: 'easeOut',
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, reducedMotion, value]);

  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
}

export function StatsBand() {
  return (
    <section className="relative border-t border-white/[0.04]">
      <div className="container py-20 lg:py-28">
        <dl className="grid grid-cols-2 gap-12 lg:grid-cols-4 lg:gap-16">
          {STATS.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="text-center"
              >
                <Icon className="mx-auto mb-4 h-5 w-5 text-primary/60" strokeWidth={1.5} aria-hidden />
                <dt className="sr-only">{stat.label}</dt>
                <dd>
                  <span className="mb-2 block text-4xl font-bold tracking-tight text-white lg:text-5xl">
                    <CountUp value={stat.value} suffix={stat.suffix} />
                  </span>
                  <span className="text-sm tracking-wide text-slate-400">{stat.label}</span>
                </dd>
              </motion.div>
            );
          })}
        </dl>
      </div>
    </section>
  );
}
